import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BAR_COLORS, FONT_FAMILY_MAP, resolveConditionalColor } from '../utils';
import { useTranslation } from 'react-i18next';

// Register essential Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const BarChartComponent = ({ widget, stats, isVertical }) => {
  const { t } = useTranslation();
  const { basicProps, advancedProps } = widget;

  const valueFontFamily = FONT_FAMILY_MAP[advancedProps.formattingFontWeight] || '';

  const data = useMemo(() => stats ?? [], [stats]);
  const values = useMemo(() => data.map((d) => d.value ?? 0), [data]);
  const total = useMemo(() => values.reduce((sum, v) => sum + v, 0), [values]);

  // Check if the data contains `yaxis` (stacked data)
  const isStacked = useMemo(() => data.some((item) => item.yaxis), [data]);

  // If the data is stacked, group it by `xaxis` and `yaxis`
  const chartData = {
    labels: isStacked
      ? [...new Set(data.map((item) => item.xaxis))] // Get unique xaxis values
      : data.map((item) => item.xaxis), // For regular data
    datasets: [],
  };

  if (isStacked) {
    // Group data by xaxis and yaxis, and sum the values
    const groupedData = data.reduce((acc, { xaxis, yaxis, value }) => {
      if (!acc[xaxis]) acc[xaxis] = {};
      if (!acc[xaxis][yaxis]) acc[xaxis][yaxis] = 0;
      acc[xaxis][yaxis] += value;
      return acc;
    }, {});

    // Create datasets for each `yaxis` value (test1, test2, etc.)
    const yaxisLabels = [...new Set(data.map((item) => item.yaxis))]; // Get unique yaxis values

    chartData.datasets = yaxisLabels.map((yaxis, datasetIndex) => {
      const datasetValues = chartData.labels.map((xaxis) => groupedData[xaxis]?.[yaxis] ?? 0);
      return {
        label: yaxis,
        data: datasetValues,
        backgroundColor:
          advancedProps.formatting === 'default'
            ? BAR_COLORS[datasetIndex % BAR_COLORS.length]
            : datasetValues.map((value) =>
                resolveConditionalColor(value, advancedProps, BAR_COLORS[0]),
              ),
      };
    });
  } else {
    // For regular data (non-stacked)
    chartData.datasets = [
      {
        label: 'Value',
        data: data.map((item) => item.value),
        backgroundColor:
          advancedProps.formatting === 'default'
            ? BAR_COLORS[0]
            : data.map((i) => resolveConditionalColor(i.value, advancedProps, BAR_COLORS[0])),
        borderSkipped: false,
      },
    ];
  }

  // 2. Configuration Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        ...(isVertical
          ? { top: basicProps.calculation === 'submissionsAverageScore' ? 40 : 20 }
          : { right: basicProps.calculation === 'submissionsAverageScore' ? 60 : 20 }),
      },
    },
    indexAxis: isVertical ? 'x' : 'y',
    plugins: {
      legend: {
        position: 'bottom',
      },
      // title: {
      //   display: true,
      //   text: basicProps.label,
      //   color: '#333',
      //   font: { size: 18 },
      //   // padding: { bottom: 20 },
      // },

      datalabels: {
        display: true,
        formatter: (value) => {
          if (basicProps.calculation === 'submissionsAverageScore') {
            const percentage = total ? ((value / total) * 100).toFixed(1) : 0;
            return `${percentage}%`;
          } else {
            return value;
          }
        },
        color: '#ffffff',
        backgroundColor: '#000000',
        borderRadius: 4,
        padding: {
          top: 4,
          bottom: 4,
          left: 6,
          right: 6,
        },
        font: {
          size: advancedProps.formattingFontSize,
          family: valueFontFamily,
        },
        anchor: basicProps.calculation === 'submissionsAverageScore' ? 'end' : 'center',
        align: basicProps.calculation === 'submissionsAverageScore' ? 'end' : 'center',
        clamp: true,
      },
    },
    scales: {
      x: {
        stacked: isStacked, // Stack x-axis if data is stacked
        grid: {
          display: true,
          drawOnChartArea: true,
          drawTicks: true,
        },
      },
      y: {
        stacked: isStacked, // Stack y-axis if data is stacked
        grid: {
          display: true,
        },
      },
    },
  };

  return !stats ? (
    <div>{t('text_loading_chart_data')}</div>
  ) : data.length === 0 ? (
    <div>No data to show</div>
  ) : (
    <div className='d-flex flex-column align-items-center'>
      <div className='d-flex flex-grow-1 w-100'>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

BarChartComponent.propTypes = {
  widget: PropTypes.object.isRequired,
  stats: PropTypes.array,
  isVertical: PropTypes.bool.isRequired,
};

export default BarChartComponent;
